import React, { useEffect, useRef, useMemo, useCallback, useState } from 'react';
import Quill from 'quill';
import 'quill/dist/quill.snow.css';
import 'quill-mention/dist/quill.mention.css';
import {Mention, MentionBlot} from "quill-mention";
import { fetchTickets } from '../api';

Quill.register({ "blots/mention": MentionBlot, "modules/mention": Mention });

const parsePx = (value, fallback) => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const match = value.trim().match(/^(\d+(?:\.\d+)?)px$/i);
    if (match) return Number(match[1]);
  }
  return fallback;
};

// Simple reusable Quill editor component.
// Props:
//  - value: HTML string to render in the editor.
//  - onChange: callback fired with updated HTML when content changes.
//  - placeholder: placeholder text.
//  - modules: custom Quill modules (optional).
//  - theme: Quill theme (default 'snow').
//  - height: height of the editor area.
//  - autoGrow: grow editor height downward with content (optional).
//  - resizable: show a bottom drag handle for vertical resize (optional).
//  - minHeight / maxHeight: bounds for autoGrow / resize.
const QuillEditor = ({
  value = '',
  onChange = () => {},
  placeholder = 'Type here…',
  modules,
  theme = 'snow',
  height = '120px',
  autoGrow = false,
  resizable = false,
  minHeight,
  maxHeight = '280px',
  style = {},
  className = '',
  mentionEmails = [],
  skipMentionFetch = false,
}) => {
  const editorRef = useRef(null);
  const quillRef = useRef(null);
  const onChangeRef = useRef(onChange);
  const mentionListRef = useRef([]);
  const dragStateRef = useRef(null);
  const resolvedMinHeight = parsePx(minHeight || height, 120);
  const resolvedMaxHeight = parsePx(maxHeight, 280);
  const [editorHeight, setEditorHeight] = useState(resolvedMinHeight);
  const editorHeightRef = useRef(resolvedMinHeight);
  const configRef = useRef({
    autoGrow,
    minHeight: resolvedMinHeight,
    maxHeight: resolvedMaxHeight,
  });

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    configRef.current = {
      autoGrow,
      minHeight: resolvedMinHeight,
      maxHeight: resolvedMaxHeight,
    };
  }, [autoGrow, resolvedMinHeight, resolvedMaxHeight]);

  useEffect(() => {
    editorHeightRef.current = editorHeight;
  }, [editorHeight]);

  // Default toolbar setup if none provided
  const defaultModules = useMemo(
    () => ({
      toolbar: [
        ['bold', 'italic', 'underline'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        ['link'],
        ['clean'],
      ],
    }),
    []
  );

  // Mention module configuration using quill-mention
  const mentionModule = useMemo(
    () => ({
      mention: {
        allowedChars: /^[A-Za-z0-9_.-]*$/, // allow typical email characters
        mentionDenotationChars: ['@'],
        source: function (searchTerm, renderList) {
          const values = mentionListRef.current;
          const matches = !searchTerm
            ? values
            : values.filter((item) =>
                item.value.toLowerCase().includes(searchTerm.toLowerCase())
              );
          const limitedMatches = matches.slice(0, 3); // Limit suggestions to 3
          console.log('[QuillEditor] mention source', {
            searchTerm,
            matchesCount: limitedMatches.length,
            totalValues: values.length,
          });
          renderList(limitedMatches, searchTerm);
        },
      },
    }),
    []
  );

  // Combine provided modules (or defaults) with mention module
  const combinedModules = useMemo(
    () => ({
      ...(modules || defaultModules),
      ...mentionModule,
    }),
    [modules, defaultModules, mentionModule]
  );

  const applyHeight = useCallback((nextHeight) => {
    if (!quillRef.current) return;
    const config = configRef.current;
    const clamped = Math.min(
      Math.max(nextHeight, config.minHeight),
      config.maxHeight
    );
    const editor = quillRef.current.root;
    const container = quillRef.current.container;
    if (!editor || !container) return;

    container.style.height = `${clamped}px`;
    editor.style.height = `${clamped}px`;
    editor.style.overflowY = 'auto';
    editorHeightRef.current = clamped;
    setEditorHeight(clamped);
  }, []);

  const adjustHeight = useCallback(() => {
    const config = configRef.current;
    if (!quillRef.current) return;

    if (!config.autoGrow) {
      applyHeight(editorHeightRef.current);
      return;
    }

    const editor = quillRef.current.root;
    if (!editor) return;

    editor.style.height = 'auto';
    editor.style.overflowY = 'hidden';
    const contentHeight = editor.scrollHeight;
    // Grow downward with content, but never shrink below the user-dragged height.
    const nextHeight = Math.min(
      Math.max(contentHeight, editorHeightRef.current, config.minHeight),
      config.maxHeight
    );
    applyHeight(nextHeight);
  }, [applyHeight]);

  const handleResizeStart = useCallback((event) => {
    event.preventDefault();
    dragStateRef.current = {
      startY: event.clientY,
      startHeight: editorHeightRef.current,
    };

    const onMove = (moveEvent) => {
      if (!dragStateRef.current) return;
      const deltaY = moveEvent.clientY - dragStateRef.current.startY;
      // Positive delta expands downward only in document flow.
      applyHeight(dragStateRef.current.startHeight + deltaY);
    };

    const onUp = () => {
      dragStateRef.current = null;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [applyHeight]);

  // Initialise Quill once on mount
  useEffect(() => {
    if (editorRef.current && !quillRef.current) {
      quillRef.current = new Quill(editorRef.current, {
        theme,
        modules: combinedModules,
        placeholder,
      });

      // When content changes, propagate up
      quillRef.current.on('text-change', () => {
        const html = quillRef.current.root.innerHTML;
        onChangeRef.current(html);
        adjustHeight();
      });

      applyHeight(resolvedMinHeight);
    }

    return () => {
      // Clean up on unmount
      quillRef.current = null;
    };
    // We intentionally ignore modules / placeholder / theme here after init
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep editor content in sync when `value` prop changes from outside
  useEffect(() => {
    if (quillRef.current && value !== quillRef.current.root.innerHTML) {
      // Preserve cursor position if possible
      const sel = quillRef.current.getSelection();
      quillRef.current.root.innerHTML = value || '';
      if (sel) {
        quillRef.current.setSelection(sel);
      }
    }
    adjustHeight();
  }, [value, adjustHeight, autoGrow, resolvedMinHeight, resolvedMaxHeight]);

  // Populate mention list from provided emails, else fallback to fetching tickets once
  useEffect(() => {
    // If the parent provides emails, use them
    if (Array.isArray(mentionEmails) && mentionEmails.length > 0) {
      const uniqueEmails = Array.from(new Set(mentionEmails.filter(Boolean)));
      mentionListRef.current = uniqueEmails.map((e) => ({ id: e, value: e }));
      return;
    }

    // If parent opted out of fallback fetching, leave list empty
    if (skipMentionFetch) {
      mentionListRef.current = [];
      return;
    }

    let cancelled = false;
    const loadMentionData = async () => {
      try {
        const tickets = await fetchTickets();
        if (cancelled) return;
        const emails = [];
        tickets.forEach((t) => {
          const addEmail = (e) => {
            if (e) emails.push(e);
          };
          addEmail(t.creator_email);
          addEmail(t.assigned_to_email);
        });
        const uniqueEmails = Array.from(new Set(emails.filter(Boolean)));
        mentionListRef.current = uniqueEmails.map((e) => ({ id: e, value: e }));
      } catch (err) {
        console.error('[QuillEditor] Failed to load mention data', err);
      }
    };
    loadMentionData();
    return () => { cancelled = true; };
  }, [mentionEmails, skipMentionFetch]);

  const editorNode = (
    <div
      ref={editorRef}
      style={
        autoGrow || resizable
          ? { height: `${editorHeight}px`, minHeight: `${resolvedMinHeight}px`, ...style }
          : { height, ...style }
      }
      className={className}
    />
  );

  if (!resizable) return editorNode;

  return (
    <div className="relative">
      {editorNode}
      <div
        role="separator"
        aria-orientation="horizontal"
        aria-label="Resize description"
        title="Drag to resize"
        onMouseDown={handleResizeStart}
        className="flex h-3 cursor-ns-resize items-center justify-center border-t border-gray-200 bg-gray-50 hover:bg-gray-100"
      >
        <div className="h-0.5 w-8 rounded-full bg-gray-400" />
      </div>
    </div>
  );
};

export default QuillEditor;
